import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Typography,
  Box,
  Button,
  Divider,
  TextField,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useTranslation } from "react-i18next";
import "../../App.css";
import { Controller, useForm } from "react-hook-form";
import { BookingApi } from "../../API/Apis/BookingApi";
import { isDogmaAiEnabled } from "../../helpers/dogmaAi";
import { DetailsFormValues } from "../../components/Layouts/WebCheckInForms/WebCheckInForms.props";
import CardWithLoader from "./CardWithLoader";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  it as itLocale,
  es as esLocale,
  fr as frLocale,
  de as deLocale,
} from "date-fns/locale";
import { itIT, esES, frFR, deDE } from "@mui/x-date-pickers/locales";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WebCheckInLayout from "./WebCheckInLayout";
import { calculateNestedProgress } from "../../helpers/calculateNestedProgress";
import ErrorIcon from "@mui/icons-material/Error";
import WebCheckInPopUp from "./WebCheckInPopUp";
import BookingGetters from "../../store/onlineBooking/getters";
import provinceData from "../../data/provinceCities.json";
import i18n from "../../config/i18n";
import DocumentScannerOverlay from "./DocumentScannerOverlay";

// ─── Mobile detection ─────────────────────────────────────────────────────────
const isMobileDevice = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return (
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || window.innerWidth <= 768
  );
};

type Props = {
  section: number;
  details: any;
  onBack: () => void;
  onNext?: (extractedData?: any) => void;
  rentalAnswer?: String;
  onShowModal?: (message: string) => void;
  documentTypes: any;
  isSmallScreen: boolean;
  stepGroups?: any;
  currentDriver: any;
  currentCustomerDetails: any;
  lowQualityUploadedImage: any;
  savedFiles?: any;
  progressCalculator?: (
    currentSection: number,
    totalSections: number
  ) => number;
  isShareboxEnabled?: boolean;
};

type FormData = {
  // Section 2
  document: string;
  documentNumber: string;
  licenseType: string;
  releaseDate: string;
  documentExpiryDate: string;
  issuedBy: string;

  // Section 3
  document1: string;
  documentNumb1: string;
  releaseDate1: string;
  expiryDate1: string;
  issueBy1: string;
};

export default function UploadDocuments({
  section,
  details,
  onBack,
  onNext,
  rentalAnswer,
  onShowModal,
  documentTypes,
  isSmallScreen,
  stepGroups,
  currentDriver,
  currentCustomerDetails,
  lowQualityUploadedImage,
  savedFiles,
  progressCalculator,
  isShareboxEnabled,
}: Props) {
  if (!details) return null;
  const lang = i18n.language;
  const dateLocale =
    lang === "it"
      ? itLocale
      : lang === "es"
      ? esLocale
      : lang === "fr"
      ? frLocale
      : lang === "de"
      ? deLocale
      : undefined;
  const muiLocaleText =
    lang === "it"
      ? itIT.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "es"
      ? esES.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "fr"
      ? frFR.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "de"
      ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
      : undefined;
  const { t: translate } = useTranslation();
  const [driverLicenseFront, setDriverLicenseFront] = useState<File | null>(
    savedFiles?.drivingLicenseDocuments?.[0] || null
  );
  const [driverLicenseBack, setDriverLicenseBack] = useState<File | null>(
    savedFiles?.drivingLicenseDocuments?.[1] || null
  );
  const [idFront, setIdFront] = useState<File | null>(
    savedFiles?.identificationDocuments?.[0] || null
  );
  const [idBack, setIdBack] = useState<File | null>(
    savedFiles?.identificationDocuments?.[1] || null
  );
  const customerDocs: string[] = savedFiles?.customerDocs || [];
  const apiLicenseDocs = customerDocs.filter((d) =>
    d.startsWith("driverLicenseDocument")
  );
  const apiIdDocs = customerDocs.filter((d) => d.startsWith("idDoc"));
  const [preUploaded] = useState({
    driverLicenseFront: apiLicenseDocs.length >= 1,
    driverLicenseBack: apiLicenseDocs.length >= 2,
    idFront: apiIdDocs.length >= 1,
    idBack: apiIdDocs.length >= 2,
  });

  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [currentSection, setCurrentSection] = useState(1);
  const [extractedData, setExtractedData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const componentMounted = useRef(true);
  const appSettings = BookingGetters.getAppSettings();
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  const webCheckinHeaderBgColor = appSettings?.webCheckinHeaderBgColor;
  const enableUppercaseInputField = appSettings?.enableUppercaseInputField;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const [documentExpiredMessage, setDocumentExpiredMessage] = useState("");
  const data =
    textResource?.[lang]?.flatMap((item: any) => item.data || []) || [];
  const [disclaimerText, setDisclaimerText] = useState<string>("");

  // ── Mobile scanner & File preview state ───────────────────────────────────
  const isMobile = isMobileDevice();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [scannerTarget, setScannerTarget] = useState<
    "driverLicenseFront" | "driverLicenseBack" | "idFront" | "idBack" | null
  >(null);

  // ── Mobile Action Selector State ──────────────────────────────────────────
  const [mobileActionOpen, setMobileActionOpen] = useState<boolean>(false);
  const [selectedActionTarget, setSelectedActionTarget] =
    useState<typeof scannerTarget>(null);

  const scannerSetterMap: Record<
    string,
    React.Dispatch<React.SetStateAction<File | null>>
  > = {
    driverLicenseFront: setDriverLicenseFront,
    driverLicenseBack: setDriverLicenseBack,
    idFront: setIdFront,
    idBack: setIdBack,
  };

  const scannerLabelMap: Record<string, string> = {
    driverLicenseFront: translate("Driver License – Front"),
    driverLicenseBack: translate("Driver License – Back"),
    idFront: translate("ID / Passport – Front"),
    idBack: translate("ID / Passport – Back"),
  };

  const openScanner = (target: typeof scannerTarget) => {
    setScannerTarget(target);
    setPendingUploadFile(null); // Ensure camera mode
    setScannerOpen(true);
  };

  const handleMobileActionClick = (target: typeof scannerTarget) => {
    setSelectedActionTarget(target);
    setMobileActionOpen(true);
  };

  const handleSelectScan = () => {
    setMobileActionOpen(false);
    openScanner(selectedActionTarget);
  };

  const handleSelectUpload = () => {
    setMobileActionOpen(false);
    if (selectedActionTarget) {
      const inputIdMap: Record<string, string> = {
        driverLicenseFront: "driver-license-front",
        driverLicenseBack: "driver-license-back",
        idFront: "id-front",
        idBack: "id-back",
      };
      document.getElementById(inputIdMap[selectedActionTarget])?.click();
    }
  };

  const handleScanCapture = (file: File) => {
    setScannerOpen(false);
    setPendingUploadFile(null);
    if (!scannerTarget) return;

    const setter = scannerSetterMap[scannerTarget];

    setFileErrors((prev) => {
      const next = { ...prev };
      delete next[scannerTarget!];
      return next;
    });
    setUploadErrors((prev) => {
      const next = { ...prev };
      delete next[scannerTarget!];
      return next;
    });

    setter(file);
    setScannerTarget(null);
  };

  const handleFileUpload =
    (target: string) => async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setFileErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[target];
        return newErrors;
      });
      setUploadErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[target];
        return newErrors;
      });

      const notAllowedMessage = translate("Not Uploaded");
      if (!file.type.startsWith("image/")) {
        setCanProceedWithoutScan(false);
        setModalMessage(translate("img_Not_Supported"));
        setOpenModal(true);
        setFileErrors((prev) => ({ ...prev, [target]: notAllowedMessage }));
        event.target.value = "";
        return;
      }

      const isBlurry = await isImageBlurry(file);
      const lowQualityUploadedImageMessage = translate("Not Uploaded");
      if (isBlurry) {
        setCanProceedWithoutScan(false);
        const newCount = scanFailCount + 1;
        setScanFailCount(newCount);
        setModalMessage(
          newCount >= MAX_SCAN_ATTEMPTS
            ? translate(
                "The image quality is not sufficient to complete the automatic scan.Please proceed by entering your details manually."
              )
            : lowQualityUploadedImage
        );
        setOpenModal(true);
        setFileErrors((prev) => ({
          ...prev,
          [target]: lowQualityUploadedImageMessage,
        }));
        event.target.value = "";
        return;
      }

      if (isMobile) {
        // ── MOBILE ONLY: Open the file in the preview frame overlay ──
        setPendingUploadFile(file);
        setScannerTarget(target as any);
        setScannerOpen(true);
      } else {
        // ── DESKTOP ONLY: Standard upload behavior without opening the frame ──
        const setter = scannerSetterMap[target];
        if (setter) {
          setter(file);
        }
      }

      event.target.value = "";
    };

  const styles = {
    uploadSection: {
      mb: 3,
    },
    sectionTitle: {
      textTransform: "uppercase",
      fontWeight: "bold",
      mb: 2,
      fontSize: "16px",
      textAlign: "center",
      color: webCheckinSecondaryColor || "#E3007B",
    },
    uploadBox: {
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
    },
    webCheckInDivider: {
      my: 3,
      backgroundColor: "black",
    },
  };

  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  // Populate expired-document message from API text resources (same pattern as DriverDetails)
  useEffect(() => {
    const expiredText = data.find(
      (item: any) => item.key === "driverLicenseExpiredText"
    );
    const disclaimerAIText = data.find(
      (item: any) => item.key === "disclaimerAIText"
    );
    setDocumentExpiredMessage(
      expiredText?.body || translate("DriverLicenseExpiredText")
    );
    setDisclaimerText(disclaimerAIText?.body || translate("DisclaimerText"));
  }, [lang, textResource, translate]);

  /**
   * Checks whether a document expiry date falls on or before the booking drop-off date.
   * Returns false when either date is missing or unparseable (safe default — no false blocks).
   */
  const isDocumentExpiredBeforeDropOff = (
    expiryDateStr: string | null | undefined,
    dropOffDateStr: string | null | undefined
  ): boolean => {
    if (!expiryDateStr || !dropOffDateStr) return false;
    const expiry = new Date(expiryDateStr);
    const dropOff = new Date(dropOffDateStr);
    if (isNaN(expiry.getTime()) || isNaN(dropOff.getTime())) return false;
    expiry.setHours(0, 0, 0, 0);
    dropOff.setHours(0, 0, 0, 0);
    return expiry <= dropOff;
  };
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      document: "DRIVER'S LICENSE",
      documentNumber: "",
      licenseType: "",
      releaseDate: "",
      documentExpiryDate: "",
      document1: "",
      documentNumb1: "",
      releaseDate1: "",
      expiryDate1: "",
      issuedBy: "",
      issueBy1: "",
    },
  });

  useEffect(() => {
    if (Object.keys(extractedData).length > 0) {
      reset(extractedData);
    } else if (section > 1) {
      reset({
        document: "",
        documentNumber: "",
        licenseType: "",
        releaseDate: "",
        documentExpiryDate: "",
        issuedBy: "",
        document1: "",
        documentNumb1: "",
        releaseDate1: "",
        expiryDate1: "",
        issueBy1: "",
      });
    }
  }, [extractedData, reset, section]);

  const totalSections = 3;
  const nestedProgress = progressCalculator
    ? progressCalculator(section, totalSections)
    : calculateNestedProgress({
        currentPage: "UploadDocuments",
        currentSection: section,
        totalSections,
        stepGroups,
        currentDriver,
      });

  const isImageBlurry = (file: File, threshold = 35): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.src = reader.result as string;
      };
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(false);
          return;
        }
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const gray: number[] = [];
        for (let i = 0; i < imageData.data.length; i += 4) {
          const r = imageData.data[i];
          const g = imageData.data[i + 1];
          const b = imageData.data[i + 2];
          gray.push(0.299 * r + 0.587 * g + 0.114 * b);
        }
        let laplacianSum = 0;
        let laplacianSqSum = 0;
        let count = 0;
        for (let y = 1; y < canvas.height - 1; y++) {
          for (let x = 1; x < canvas.width - 1; x++) {
            const i = y * canvas.width + x;
            const laplacian =
              -4 * gray[i] +
              gray[i - 1] +
              gray[i + 1] +
              gray[i - canvas.width] +
              gray[i + canvas.width];
            laplacianSum += laplacian;
            laplacianSqSum += laplacian * laplacian;
            count++;
          }
        }
        const mean = laplacianSum / count;
        const variance = laplacianSqSum / count - mean * mean;
        resolve(variance < threshold);
      };
      reader.readAsDataURL(file);
    });
  };

  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [modalMessage, setModalMessage] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [scanFailCount, setScanFailCount] = useState(0);
  const [canProceedWithoutScan, setCanProceedWithoutScan] = useState(false);
  const MAX_SCAN_ATTEMPTS = 1;

  const handleClose = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleProceedWithoutScan = useCallback(async () => {
    setOpenModal(false);
    onNext?.({
      extractedData: {},
      files: { driverLicenseFront, driverLicenseBack, idFront, idBack },
    });
    if (rentalAnswer === "yes") {
      await saveDataInBackground({});
    }
  }, [
    onNext,
    driverLicenseFront,
    driverLicenseBack,
    idFront,
    idBack,
    rentalAnswer,
    section,
    details,
    appSettings,
  ]);

  const uploadFile = async (file: File | null, fileType: string) => {
    if (!file) {
      return null;
    }

    try {
      const isIdScan = fileType.includes("id") ? "true" : "false";
      const isDrivingLicenceScan = fileType.includes("driverLicense")
        ? "true"
        : "false";

      const data = {
        isIdScan,
        isDrivingLicenceScan,
      };

      const response = await BookingApi.uploadDocumentsAndGetCusDetails(
        file,
        data
      );

      const apiDatastatus = response?.data?.status;
      const apiDataErrorMessage = response?.data?.errorMessage;
      if (componentMounted.current && apiDatastatus === "success") {
        return { ok: true, response };
      } else if (
        componentMounted.current &&
        apiDataErrorMessage === "Image format not supported"
      ) {
        return { ok: false, errorKey: "img_Not_Supported" };
      } else {
        return { ok: false, errorKey: "unable_to_read" };
      }
    } catch (error) {
      console.error("Upload error:", error);
      return { ok: false, errorKey: "unable_to_read" };
    }
  };

  useEffect(() => {
    const savedData = localStorage.getItem("extractedData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setExtractedData(parsedData);
      reset(parsedData);
    }
  }, [reset]);

  const extractProvinceFromAuthority = (authority) => {
    if (!authority) return "ROMA";
    const parts = authority.split("-");
    const afterHyphen = parts[1];
    const clean = afterHyphen.replace(/[^A-Za-z]/g, "");
    const provinceCode = clean.substring(0, 2).toUpperCase();
    const foundProvince = provinceData.find(
      (item) => item.Province === provinceCode
    );
    return foundProvince ? foundProvince["Main City"] : "ROMA";
  };

  const handleContinue = async () => {
    try {
      setIsLoading(true);
      if (section === 1) {
        const errors: Record<string, string> = {};
        if (!driverLicenseFront && !preUploaded.driverLicenseFront)
          errors.driverLicenseFront = translate(
            "Please upload Driver License Front"
          );
        if (!driverLicenseBack && !preUploaded.driverLicenseBack)
          errors.driverLicenseBack = translate(
            "Please upload Driver License Back"
          );
        if (!idFront && !preUploaded.idFront)
          errors.idFront = translate("Please upload ID / Passport Front");
        if (!idBack && !preUploaded.idBack)
          errors.idBack = translate("Please upload ID / Passport Back");
        if (Object.keys(errors).length > 0) {
          setUploadErrors(errors);
          setIsLoading(false);
          return;
        }
        setUploadErrors({});

        // When this tenant's AI document-scan preference is off, skip the
        // OCR extraction calls and just carry the raw files forward as-is.
        if (!isDogmaAiEnabled(appSettings)) {
          await handleProceedWithoutScan();
          return;
        }

        const uploads: Promise<any>[] = [];
        const fileTypes: string[] = [];

        // Upload driver license files
        if (driverLicenseFront) {
          uploads.push(uploadFile(driverLicenseFront, "driverLicenseFront"));
          fileTypes.push("driverLicenseFront");
        }
        if (driverLicenseBack) {
          uploads.push(uploadFile(driverLicenseBack, "driverLicenseBack"));
          fileTypes.push("driverLicenseBack");
        }

        // Upload ID/Passport files
        if (idFront) {
          uploads.push(uploadFile(idFront, "idFront"));
          fileTypes.push("idFront");
        }
        if (idBack) {
          uploads.push(uploadFile(idBack, "idBack"));
          fileTypes.push("idBack");
        }
        if (uploads.length > 0 && rentalAnswer === "yes") {
          setIsLoading(true);
        }

        const results = await Promise.all(uploads);

        const inputIdMap: Record<string, string> = {
          driverLicenseFront: "driver-license-front",
          driverLicenseBack: "driver-license-back",
          idFront: "id-front",
          idBack: "id-back",
        };
        const newFileErrors: Record<string, string> = {};
        let firstErrorKey = "";
        const failingErrorKeys: string[] = [];
        results.forEach((result, index) => {
          if (!result?.ok) {
            const fileType = fileTypes[index];
            // A file that uploaded fine but couldn't be auto-read isn't
            // "Not Uploaded" -- only flag fields with a real upload problem.
            if (result?.errorKey !== "unable_to_read") {
              newFileErrors[fileType] = translate("Not Uploaded");
            }
            if (!firstErrorKey && result?.errorKey)
              firstErrorKey = result.errorKey;
            if (result?.errorKey) failingErrorKeys.push(result.errorKey);
            const input = document.getElementById(
              inputIdMap[fileType]
            ) as HTMLInputElement | null;
            if (input) input.value = "";
          }
        });
        if (failingErrorKeys.length > 0) {
          if (Object.keys(newFileErrors).length > 0) {
            setFileErrors((prev) => ({ ...prev, ...newFileErrors }));
          }
          setCanProceedWithoutScan(
            failingErrorKeys.every((key) => key === "unable_to_read")
          );
          const newCount = scanFailCount + 1;
          setScanFailCount(newCount);
          setModalMessage(
            newCount >= MAX_SCAN_ATTEMPTS
              ? translate("unable_to_read")
              : translate(firstErrorKey || "unable_to_read")
          );
          setOpenModal(true);
          return;
        }

        const hasErrors = results.some((r) => !r?.ok);

        if (!hasErrors) {
          let driverLicenseData = {};
          let idData = {};
          const idDataBack = {};

          results.forEach((result, index) => {
            const extracted = result?.response?.data?.data;
            const fileType = fileTypes[index];

            if (fileType.includes("driverLicense")) {
              driverLicenseData = { ...driverLicenseData, ...extracted };
            } else if (fileType.includes("idFront")) {
              idData = { ...idData, ...extracted };
            } else if (fileType === "idBack") {
              if (extracted?.address) {
                idDataBack.address = extracted.address;
              }
            }
          });

          // Map driver license data for section 2
          let driverMappedData = {};
          if (driverLicenseData && Object.keys(driverLicenseData).length > 0) {
            driverMappedData = {
              document: driverLicenseData?.document || "DRIVER'S LICENSE",
              documentNumber:
                driverLicenseData?.licenseNumber ||
                driverLicenseData?.idNumber ||
                "",
              licenseType: driverLicenseData?.licenseCategory || "",
              releaseDate: driverLicenseData?.dateOfIssue || "",
              documentExpiryDate: driverLicenseData?.expiryDate || "",
              issuedBy: driverLicenseData?.issuingAuthority
                ? extractProvinceFromAuthority(
                    driverLicenseData.issuingAuthority
                  )
                : "ROMA",
              firstName: driverLicenseData?.firstName || "",
              lastName: driverLicenseData?.surname || "",
              dateOfBirth: driverLicenseData?.DOB || "",
            };
          }

          // Map ID/passport data for section 3
          let idMappedData = {};
          if (idData && Object.keys(idData).length > 0) {
            idMappedData = {
              document1:
                documentTypeMap[(idData as any)?.documentType] ||
                (idData as any)?.documentType ||
                "",
              documentNumb1: idData?.licenseNumber || idData?.idNumber || "",
              releaseDate1: idData?.dateOfIssue || "",
              expiryDate1: idData?.expiryDate || "",
              issueBy1: idData?.placeOfIssue || "",
            };

            if (rentalAnswer === "no" || currentDriver > 0) {
              idMappedData = {
                ...idMappedData,
                country: ["ITA", "ITALY", "IT"].includes(idData?.nationality)
                  ? "ITALY"
                  : "",
                sex:
                  idData?.gender === "F"
                    ? "FEMALE"
                    : idData?.gender === "M"
                    ? "MALE"
                    : "",
              };
            }
          }

          if (
            idDataBack &&
            Object.keys(idDataBack).length > 0 &&
            (rentalAnswer === "no" || currentDriver > 0)
          ) {
            const addressString = idDataBack?.address || "";
            let streetName = "";
            let streetNumber = "";
            let city = "";
            let state = "";
            if (addressString) {
              const match = addressString.match(
                /^([^,]+),\s*(?:N\.\s*)?(\d+)?\s*([^(]+)\s*\(([^)]+)\)$/
              );
              if (match) {
                streetName = (match[1] || "").trim();
                streetNumber = (match[2] || "").trim();
                city = (match[3] || "").trim();
                state = (match[4] || "").trim();
              }
            }
            idMappedData = {
              ...idMappedData,
              streetName,
              streetNumber,
              city,
              state,
            };
          }
          const combinedData = { ...driverMappedData, ...idMappedData };

          // Block if any extracted expiry date is on or before the drop-off date
          const dropOffDate = details?.dropOffDate;
          if (
            isDocumentExpiredBeforeDropOff(
              combinedData.documentExpiryDate,
              dropOffDate
            ) ||
            isDocumentExpiredBeforeDropOff(
              combinedData.expiryDate1,
              dropOffDate
            )
          ) {
            setCanProceedWithoutScan(false);
            setModalMessage(documentExpiredMessage);
            setOpenModal(true);
            return;
          }

          setExtractedData(combinedData);
          localStorage.setItem("extractedData", JSON.stringify(combinedData));
          reset(combinedData);

          onNext?.({
            extractedData: combinedData,
            files: {
              driverLicenseFront,
              driverLicenseBack,
              idFront,
              idBack,
            },
          });

          if (rentalAnswer === "yes") {
            saveDataInBackground(combinedData);
          }
        }
      } else {
        const formData = getValues();
        const dropOffDate = details?.dropOffDate;

        // Section 2: driver license expiry reviewed/edited by user
        if (
          section === 2 &&
          isDocumentExpiredBeforeDropOff(
            formData.documentExpiryDate,
            dropOffDate
          )
        ) {
          setCanProceedWithoutScan(false);
          setModalMessage(documentExpiredMessage);
          setOpenModal(true);
          return;
        }

        // Section 3: ID/Passport expiry reviewed/edited by user
        if (
          section === 3 &&
          isDocumentExpiredBeforeDropOff(formData.expiryDate1, dropOffDate)
        ) {
          setCanProceedWithoutScan(false);
          setModalMessage(documentExpiredMessage);
          setOpenModal(true);
          return;
        }

        onNext?.(formData);

        if (rentalAnswer === "yes") {
          saveDataInBackground(formData);
        }
      }
    } catch (error) {
      console.error("Error in handleContinue:", error);
    } finally {
      if (componentMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const saveDataInBackground = useCallback(
    async (data: any) => {
      setIsLoading(true);
      try {
        if (section === 1) {
          if (driverLicenseBack || driverLicenseFront || idFront || idBack) {
            await BookingApi.addDocuments(
              parseInt(details.customerId),
              [idFront, idBack].filter(Boolean) as File[],
              [driverLicenseFront, driverLicenseBack].filter(Boolean) as File[]
            );
          }
        } else {
          const excludedKeys = ["firstName", "lastName", "dateOfBirth"];
          const filteredData = Object.fromEntries(
            Object.entries(data as any).filter(
              ([key, value]) =>
                !excludedKeys.includes(key) &&
                value !== "" &&
                value !== null &&
                value !== undefined
            )
          );

          if (Object.keys(filteredData).length > 0) {
            await BookingApi.updateCustomerDetails(
              parseInt(details.customerId),
              filteredData as DetailsFormValues
            );
          }
        }
      } catch (error) {
        console.error("Error saving data:", error);
      } finally {
        if (componentMounted.current) {
          setIsLoading(false);
        }
      }
    },
    [section, driverLicenseFront, driverLicenseBack, idFront, idBack, details]
  );

  const handleBack = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    } else {
      onBack();
    }
  };

  const validateDate = (value: any) => {
    if (!value) return true;
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return translate("Invalid date");
    if (d.getFullYear() > 3000) return translate("Invalid date");
    return true;
  };

  const UploadCardFront = ({ title, onUpload, file, fileType }) => {
    const errorMessage = fileErrors[fileType];

    return (
      <Box onClick={onUpload} component="div">
        <>
          <Typography sx={{ textAlign: "center" }}>
            {translate("Front")}
          </Typography>
          <label htmlFor="voucher-upload">
            <Button
              variant="contained"
              component="span"
              sx={{
                px: 3.2,
                py: 2.3,
                marginTop: 1,
                fontWeight: "bold",
                position: "relative",
                border: errorMessage
                  ? "2px solid #FF0000"
                  : "2px solid #19344FCC",
                borderRadius: 2,
                backgroundColor: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#FFFFFF",
                  boxShadow: "none",
                },
              }}
            >
              <img
                src="/assets/icons/front.png"
                alt="upload"
                loading="lazy"
                decoding="async"
                style={{
                  width: isSmallScreen ? 45 : 60,
                  height: isSmallScreen ? 30 : 40,
                  marginRight: isSmallScreen ? 8 : 20,
                }}
              />
              {file && !errorMessage ? (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: webCheckinHeaderBgColor || "#4CAF50",
                    fontSize: 20,
                    backgroundColor: "white",
                    borderRadius: "50%",
                  }}
                />
              ) : errorMessage ? (
                <ErrorIcon
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#FF0000",
                    fontSize: 20,
                    backgroundColor: "white",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <img
                  src="/assets/icons/upload.png"
                  alt="upload"
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: 25,
                    height: 25,
                    marginRight: 10,
                    filter: "opacity(0.2) drop-shadow(0 0 0 #19344F)",
                  }}
                />
              )}
            </Button>
          </label>

          {file && !errorMessage && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: webCheckinHeaderBgColor || "#4CAF50",
                fontWeight: "bold",
              }}
            >
              {translate("Uploaded")}
            </Typography>
          )}

          {errorMessage && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: "#FF0000",
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              {errorMessage}
            </Typography>
          )}
        </>
      </Box>
    );
  };

  const UploadCardBack = ({ title, onUpload, file, fileType }) => {
    const errorMessage = fileErrors[fileType];

    return (
      <Box onClick={onUpload} component="div">
        <>
          <Typography sx={{ textAlign: "center" }}>
            {translate("Back")}
          </Typography>
          <label htmlFor="voucher-upload">
            <Button
              variant="contained"
              component="span"
              sx={{
                px: 3.2,
                py: 2.3,
                marginTop: 1,
                fontWeight: "bold",
                position: "relative",
                border: errorMessage
                  ? "2px solid #FF0000"
                  : "2px solid #19344FCC",
                borderRadius: 2,
                backgroundColor: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#FFFFFF",
                  boxShadow: "none",
                },
              }}
            >
              <img
                src="/assets/icons/back.png"
                alt="upload"
                loading="lazy"
                decoding="async"
                style={{
                  width: isSmallScreen ? 45 : 60,
                  height: isSmallScreen ? 30 : 40,
                  marginRight: isSmallScreen ? 8 : 20,
                }}
              />
              {file && !errorMessage ? (
                <CheckCircleIcon
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: webCheckinHeaderBgColor || "#4CAF50",
                    fontSize: 20,
                    backgroundColor: "white",
                    borderRadius: "50%",
                  }}
                />
              ) : errorMessage ? (
                <ErrorIcon
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "#FF0000",
                    fontSize: 20,
                    backgroundColor: "white",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <img
                  src="/assets/icons/upload.png"
                  alt="upload"
                  loading="lazy"
                  decoding="async"
                  style={{
                    width: 25,
                    height: 25,
                    marginRight: 10,
                    filter: "opacity(0.2) drop-shadow(0 0 0 #19344F)",
                  }}
                />
              )}
            </Button>
          </label>

          {file && !errorMessage && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: webCheckinHeaderBgColor || "#4CAF50",
                fontWeight: "bold",
              }}
            >
              {translate("Uploaded")}
            </Typography>
          )}

          {errorMessage && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: "#FF0000",
                fontWeight: "bold",
                fontSize: "0.75rem",
              }}
            >
              {errorMessage}
            </Typography>
          )}
        </>
      </Box>
    );
  };

  const documentTypeMap: Record<string, string> = {
    ID_CARD: "CARTA D'IDENTITA'",
    PASSPORT: "PASSAPORTO",
    DIPLOMATIC_ID_CARD: "CARTA ID. DIPLOMATICA",
    ELECTRONIC_ID_CARD: "CARTA IDENTITA' ELETTRONICA",
    DIPLOMATIC_PASSPORT: "PASSAPORTO DIPLOMATICO",
    ORDINARY_PASSPORT: "PASSAPORTO ORDINARIO",
    SERVICE_PASSPORT: "PASSAPORTO DI SERVIZIO",
    CI: "CI",
    "CARTA DI IDENTITA'": "CARTA DI IDENTITA'",
    PASSAPORTO: "PASSAPORTO",
    "CARTA ID. DIPLOMATICA": "CARTA ID. DIPLOMATICA",
    "CARTA IDENTITA' ELETTRONICA": "CARTA IDENTITA' ELETTRONICA",
    "PASSAPORTO DIPLOMATICO": "PASSAPORTO DIPLOMATICO",
    "PASSAPORTO ORDINARIO": "PASSAPORTO ORDINARIO",
    "PASSAPORTO DI SERVIZIO": "PASSAPORTO DI SERVIZIO",
  };

  const fallbackDocumentTypes = [
    "PATENTE",
    "CARTA D'IDENTITA'",
    "PASSAPORTO",
    "CARTA ID. DIPLOMATICA",
    "CARTA IDENTITA' ELETTRONICA",
    "CARTA DI IDENTITA'",
    "CI",
    "PASSAPORTO DIPLOMATICO",
    "PASSAPORTO ORDINARIO",
    "PASSAPORTO DI SERVIZIO",
  ];

  const filteredDocumentTypes = (
    Array.isArray(documentTypes) && documentTypes.length > 0
      ? documentTypes
      : fallbackDocumentTypes
  ).filter((docType: string) => docType !== "PATENTE DI GUIDA");

  const renderSection1 = () => (
    <>
      {/* ── Mobile Action Menu Dialog ── */}
      <Dialog
        open={mobileActionOpen}
        onClose={() => setMobileActionOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, padding: 1, minWidth: "280px" } }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          {translate("Select Option") || "Select Option"}
        </DialogTitle>
        <List>
          <ListItem button onClick={handleSelectScan}>
            <ListItemIcon>
              <CameraAltIcon
                sx={{ color: webCheckinSecondaryColor || "#00C896" }}
              />
            </ListItemIcon>
            <ListItemText
              primary={translate("Scan with Camera") || "Scan with Camera"}
            />
          </ListItem>
          <ListItem button onClick={handleSelectUpload}>
            <ListItemIcon>
              <PhotoLibraryIcon
                sx={{ color: webCheckinSecondaryColor || "#00C896" }}
              />
            </ListItemIcon>
            <ListItemText
              primary={translate("Upload from Device") || "Upload from Device"}
            />
          </ListItem>
        </List>
      </Dialog>

      <Box sx={styles.uploadSection}>
        <Typography sx={styles.sectionTitle}>
          {translate("Drive Licence")}
        </Typography>
        <Box sx={styles.uploadBox}>
          <Box>
            <input
              type="file"
              id="driver-license-front"
              style={{ display: "none" }}
              onChange={handleFileUpload("driverLicenseFront")}
              accept="image/*"
            />
            <label
              htmlFor={!isMobile ? "driver-license-front" : undefined}
              onClick={
                isMobile
                  ? () => handleMobileActionClick("driverLicenseFront")
                  : undefined
              }
              style={{ cursor: "pointer" }}
            >
              <UploadCardFront
                title="Front"
                file={
                  driverLicenseFront ||
                  (preUploaded.driverLicenseFront ? ({} as File) : null)
                }
                fileType="driverLicenseFront"
                onUpload={
                  isMobile
                    ? () => handleMobileActionClick("driverLicenseFront")
                    : () =>
                        document.getElementById("driver-license-front")?.click()
                }
              />
            </label>
            {uploadErrors.driverLicenseFront && (
              <Typography variant="caption" color="error">
                {uploadErrors.driverLicenseFront}
              </Typography>
            )}
          </Box>

          <Box>
            <input
              type="file"
              id="driver-license-back"
              style={{ display: "none" }}
              onChange={handleFileUpload("driverLicenseBack")}
              accept="image/*"
            />
            <label
              htmlFor={!isMobile ? "driver-license-back" : undefined}
              onClick={
                isMobile
                  ? () => handleMobileActionClick("driverLicenseBack")
                  : undefined
              }
              style={{ cursor: "pointer" }}
            >
              <UploadCardBack
                title="Back"
                file={
                  driverLicenseBack ||
                  (preUploaded.driverLicenseBack ? ({} as File) : null)
                }
                fileType="driverLicenseBack"
                onUpload={
                  isMobile
                    ? () => handleMobileActionClick("driverLicenseBack")
                    : () =>
                        document.getElementById("driver-license-back")?.click()
                }
              />
            </label>
            {uploadErrors.driverLicenseBack && (
              <Typography variant="caption" color="error">
                {uploadErrors.driverLicenseBack}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Divider sx={styles.webCheckInDivider} />

      {/* ID/Passport Section */}
      <Box sx={styles.uploadSection}>
        <Typography sx={styles.sectionTitle}>
          {translate("ID / Passport")}
        </Typography>
        <Box sx={styles.uploadBox}>
          <Box>
            <input
              type="file"
              id="id-front"
              style={{ display: "none" }}
              onChange={handleFileUpload("idFront")}
              accept="image/*"
            />
            <label
              htmlFor={!isMobile ? "id-front" : undefined}
              onClick={
                isMobile ? () => handleMobileActionClick("idFront") : undefined
              }
              style={{ cursor: "pointer" }}
            >
              <UploadCardFront
                title="Front"
                file={idFront || (preUploaded.idFront ? ({} as File) : null)}
                fileType="idFront"
                onUpload={
                  isMobile
                    ? () => handleMobileActionClick("idFront")
                    : () => document.getElementById("id-front")?.click()
                }
              />
            </label>
            {uploadErrors.idFront && (
              <Typography variant="caption" color="error">
                {uploadErrors.idFront}
              </Typography>
            )}
          </Box>

          <Box>
            <input
              type="file"
              id="id-back"
              style={{ display: "none" }}
              onChange={handleFileUpload("idBack")}
              accept="image/*"
            />
            <label
              htmlFor={!isMobile ? "id-back" : undefined}
              onClick={
                isMobile ? () => handleMobileActionClick("idBack") : undefined
              }
              style={{ cursor: "pointer" }}
            >
              <UploadCardBack
                title="Back"
                file={idBack || (preUploaded.idBack ? ({} as File) : null)}
                fileType="idBack"
                onUpload={
                  isMobile
                    ? () => handleMobileActionClick("idBack")
                    : () => document.getElementById("id-back")?.click()
                }
              />
            </label>
            {uploadErrors.idBack && (
              <Typography variant="caption" color="error">
                {uploadErrors.idBack}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Added Text Section */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          borderLeft: `6px solid ${webCheckinHeaderBgColor || "#E3007B"}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 400,
            fontSize: "1rem",
            lineHeight: 1.5,
          }}
        >
          {disclaimerText}
        </Typography>
      </Box>
    </>
  );

  const renderSection2 = () => (
    <>
      <Controller
        name="document"
        control={control}
        defaultValue="DRIVER'S LICENSE"
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Type of Document")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              value={
                isShareboxEnabled
                  ? translate("DRIVER'S LICENSE")
                  : "DRIVER'S LICENSE"
              }
              InputProps={{ readOnly: true }}
              className="webCheckInTextField"
            />
          </>
        )}
      />

      <Controller
        name="documentNumber"
        control={control}
        defaultValue=""
        rules={{ required: "Document Number is required" }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Document Number")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.documentNumber}
              helperText={errors.documentNumber?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />

      <Controller
        name="licenseType"
        control={control}
        defaultValue=""
        rules={{ required: "License Type is required" }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("License Type")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.licenseType}
              helperText={errors.licenseType?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="releaseDate"
              control={control}
              defaultValue=""
              rules={{
                required: "Release Date is required",
                validate: validateDate,
              }}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Release Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.releaseDate}
                        helperText={errors.releaseDate?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="documentExpiryDate"
              control={control}
              defaultValue=""
              rules={{
                required: "Expire Date is required",
                validate: validateDate,
              }}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Expiry Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.documentExpiryDate}
                        helperText={errors.documentExpiryDate?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Controller
        name="issuedBy"
        control={control}
        defaultValue=""
        rules={{ required: "Release By is required" }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Release by")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.issuedBy}
              helperText={errors.issuedBy?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />
    </>
  );

  const renderSection3 = () => (
    <>
      <Controller
        name="document1"
        control={control}
        defaultValue=""
        rules={{ required: "Type Of Document is required" }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Type of Document")}
            </Typography>
            <TextField
              {...field}
              select
              fullWidth
              error={!!errors.document1}
              helperText={errors.document1?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              value={documentTypeMap[field.value] || field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
              }}
            >
              {filteredDocumentTypes.map((docType: string, index: number) => (
                <MenuItem key={index} value={docType}>
                  {docType}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      />

      <Controller
        name="documentNumb1"
        control={control}
        rules={{ required: "Document Number is required" }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Document Number")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.documentNumb1}
              helperText={errors.documentNumb1?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="releaseDate1"
              control={control}
              defaultValue=""
              rules={{
                required: "Release Date is required",
                validate: validateDate,
              }}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Release Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.releaseDate1}
                        helperText={errors.releaseDate1?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="expiryDate1"
              control={control}
              defaultValue=""
              rules={{
                required: "Expiry Date is required",
                validate: validateDate,
              }}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Expiry Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.expiryDate1}
                        helperText={errors.expiryDate1?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Controller
        name="issueBy1"
        control={control}
        rules={{ required: "Release By is required" }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Release by")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.issueBy1}
              helperText={errors.issueBy1?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />
    </>
  );

  return (
    <CardWithLoader isLoading={isLoading}>
      <WebCheckInLayout
        title={
          section === 1
            ? translate("Upload Documents")
            : translate("Driver Details")
        }
        progress={nestedProgress}
        onBack={handleBack}
      >
        <form onSubmit={handleSubmit(handleContinue)}>
          {section === 1 && renderSection1()}
          {section === 2 && renderSection2()}
          {section === 3 && renderSection3()}
          <Box textAlign="center">
            <Button
              variant="contained"
              type={section === 1 ? "button" : "submit"}
              onClick={section === 1 ? handleContinue : undefined}
              className="webCheckInButton"
              style={{
                borderRadius: `${appSettings?.buttonBorderRadius || 55}px`,
              }}
              sx={
                isShareboxEnabled
                  ? {
                      width: "100% !important",
                      maxWidth: "none !important",
                      borderRadius: "10px !important",
                      border: `2px solid ${
                        appSettings?.webCheckinButtonColor || "#19344FCC"
                      } !important`,
                    }
                  : undefined
              }
            >
              {translate("Continue")}
            </Button>
          </Box>
        </form>
      </WebCheckInLayout>
      {openModal && (
        <WebCheckInPopUp
          openModal={openModal}
          modalMessage={modalMessage}
          onRequestClose={handleClose}
          isSmallScreen={isSmallScreen}
          isMaxAttempts={canProceedWithoutScan}
          onProceed={handleProceedWithoutScan}
        />
      )}

      {/* ── Mobile & Desktop Document Scanner / Preview Overlay ── */}
      {(scannerOpen || pendingUploadFile) && scannerTarget && (
        <DocumentScannerOverlay
          label={scannerLabelMap[scannerTarget]}
          uploadFile={pendingUploadFile}
          onCapture={handleScanCapture}
          onClose={() => {
            setScannerOpen(false);
            setScannerTarget(null);
            setPendingUploadFile(null);
          }}
        />
      )}
    </CardWithLoader>
  );
}
